import { Injectable } from '@angular/core';
import { INDEXED_DB_NAME, INDEXED_DB_STORE_NAME } from '@core/constants';
import {
  IndexedDbBatchOperation,
  IndexedDbEntry,
  IndexedDbStoredItem,
} from './indexed-db.types';

@Injectable({
  providedIn: 'root',
})
export class IndexedDbService {
  private dbName = INDEXED_DB_NAME;
  private dbVersion = 1;
  private storeName = INDEXED_DB_STORE_NAME;
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB
   * Lazy initialization - only runs when first method is called
   */
  private async initDB(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    if (typeof indexedDB === 'undefined') {
      console.warn('IndexedDB is not available');

      return;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB', request.error);
        reject(this.createError(request.error, 'Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Ensure DB is initialized
   */
  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.initDB();
    }

    if (!this.db) {
      throw new Error('IndexedDB is not available');
    }

    return this.db;
  }

  /**
   * Create error with fallback message
   */
  private createError(
    error: DOMException | null | undefined,
    fallback: string,
  ): Error {
    return new Error(error?.message || fallback);
  }

  /**
   * Execute transaction and return result
   */
  private async executeTransaction<T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>,
    errorMessage: string,
  ): Promise<T> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], mode);
      const store = transaction.objectStore(this.storeName);
      const request = operation(store);
      let result: T;

      request.onsuccess = () => {
        result = request.result;
      };
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => {
        console.error(errorMessage, request.error);
        reject(this.createError(request.error, errorMessage));
      };
      transaction.onabort = () => {
        reject(this.createError(transaction.error, errorMessage));
      };
    });
  }

  private createStoredItem<T>(
    value: T,
    ttlMinutes?: number,
  ): IndexedDbStoredItem<T> {
    const ttlMilliseconds = ttlMinutes ? ttlMinutes * 60 * 1000 : null;

    return {
      value,
      expiry: ttlMilliseconds ? Date.now() + ttlMilliseconds : null,
    };
  }

  /**
   * Set item in IndexedDB with optional TTL
   */
  async setItem<T>(key: string, value: T, ttlMinutes?: number): Promise<void> {
    try {
      await this.executeTransaction(
        'readwrite',
        store => store.put(this.createStoredItem(value, ttlMinutes), key),
        'Failed to set item in IndexedDB',
      );
    } catch (error) {
      console.error('Error setting item in IndexedDB', error);
      throw error;
    }
  }

  async executeBatch(operation: IndexedDbBatchOperation): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      let requestError: DOMException | null = null;

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => {
        const error = this.createError(
          requestError ?? transaction.error,
          'Failed to execute IndexedDB batch',
        );

        console.error(error.message, requestError ?? transaction.error);
        reject(error);
      };
      transaction.onabort = () => {
        reject(
          this.createError(
            requestError ?? transaction.error,
            'IndexedDB batch transaction was aborted',
          ),
        );
      };

      for (const put of operation.puts ?? []) {
        const request = store.put(
          this.createStoredItem(put.value, put.ttlMinutes),
          put.key,
        );

        request.onerror = () => {
          requestError = request.error;
          transaction.abort();
        };
      }

      for (const key of operation.deletes ?? []) {
        const request = store.delete(key);

        request.onerror = () => {
          requestError = request.error;
          transaction.abort();
        };
      }
    });
  }

  /**
   * Get item from IndexedDB
   */
  async getItem<T>(key: string): Promise<T | null> {
    try {
      const item = await this.executeTransaction<
        IndexedDbStoredItem<T> | undefined
      >(
        'readonly',
        store => store.get(key),
        'Failed to get item from IndexedDB',
      );

      if (!item) {
        return null;
      }

      const now = new Date();

      // Check if item has expired
      if (item.expiry && now.getTime() > item.expiry) {
        await this.removeItem(key);

        return null;
      }

      return item.value;
    } catch (error) {
      console.error('Error getting item from IndexedDB', error);
      throw error;
    }
  }

  /**
   * Remove item from IndexedDB
   */
  async removeItem(key: string): Promise<void> {
    try {
      await this.executeTransaction(
        'readwrite',
        store => store.delete(key),
        'Failed to remove item from IndexedDB',
      );
    } catch (error) {
      console.error('Error removing item from IndexedDB', error);
      throw error;
    }
  }

  /**
   * Clear all items from IndexedDB
   */
  async clear(): Promise<void> {
    try {
      await this.executeTransaction(
        'readwrite',
        store => store.clear(),
        'Failed to clear IndexedDB',
      );
    } catch (error) {
      console.error('Error clearing IndexedDB', error);
      throw error;
    }
  }

  /**
   * Get all keys from IndexedDB
   */
  async getAllKeys(): Promise<string[]> {
    try {
      return (await this.executeTransaction(
        'readonly',
        store => store.getAllKeys(),
        'Failed to get all keys from IndexedDB',
      )) as string[];
    } catch (error) {
      console.error('Error getting all keys from IndexedDB', error);
      throw error;
    }
  }

  /**
   * Get all non-expired entries from IndexedDB in a single transaction
   * (cheaper than getAllKeys() + one getItem() per key)
   */
  async getAllEntries<T>(): Promise<IndexedDbEntry<T>[]> {
    try {
      const db = await this.ensureDB();

      return await new Promise<IndexedDbEntry<T>[]>((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.openCursor();
        const now = Date.now();
        const entries: IndexedDbEntry<T>[] = [];

        request.onsuccess = () => {
          const cursor = request.result;

          if (!cursor) {
            return;
          }

          const stored = cursor.value as IndexedDbStoredItem<T>;

          if (!stored.expiry || now <= stored.expiry) {
            entries.push({ key: cursor.key as string, value: stored.value });
          }

          cursor.continue();
        };

        transaction.oncomplete = () => resolve(entries);
        transaction.onerror = () => {
          console.error(
            'Failed to get all entries from IndexedDB',
            transaction.error,
          );
          reject(
            this.createError(
              transaction.error,
              'Failed to get all entries from IndexedDB',
            ),
          );
        };
        transaction.onabort = () => {
          reject(
            this.createError(
              transaction.error,
              'IndexedDB getAllEntries transaction was aborted',
            ),
          );
        };
      });
    } catch (error) {
      console.error('Error getting all entries from IndexedDB', error);
      throw error;
    }
  }
}
